/**
 * Chatbase API v2 SSE helpers.
 *
 * Extracted from ChatPage.jsx so we can unit-test the frame shape and the finish-frame
 * `conversationId` lookup against real payloads captured from the live API.
 *
 * Chatbase docs: https://www.chatbase.co/docs/api-v2
 * A typical stream looks like:
 *
 *     data: {"type":"start","messageId":"..."}
 *     data: {"type":"start-step"}
 *     data: {"type":"text-start","id":"0"}
 *     data: {"type":"text-delta","id":"0","delta":"Hello"}
 *     data: {"type":"text-end","id":"0"}
 *     data: {"type":"finish-step"}
 *     data: {"type":"finish","finishReason":"stop",
 *            "messageMetadata":{"conversationId":"...","messageId":"...","usage":{...}}}
 *     data: [DONE]
 *
 * IMPORTANT: `conversationId` is nested inside `messageMetadata` — NOT under `metadata`
 * and NOT at the top level. We keep older aliases (`metadata`, top-level `conversationId`)
 * as defensive fallbacks in case Chatbase renames the shape again.
 */

/**
 * Consume the streaming `buffer` string, split it on SSE frame boundaries, and return
 * `{ events, rest }` where `rest` is the tail that hasn't yet finished a frame.
 */
export function parseSseChunk(buffer) {
  const events = [];
  let idx;
  while ((idx = buffer.indexOf("\n\n")) !== -1) {
    const raw = buffer.slice(0, idx);
    buffer = buffer.slice(idx + 2);
    for (const line of raw.split("\n")) {
      if (!line.startsWith("data:")) continue;
      const payload = line.slice(5).trim();
      if (!payload || payload === "[DONE]") continue;
      try {
        events.push(JSON.parse(payload));
      } catch {
        // Some Chatbase SSE frames are plain-text tokens rather than JSON — treat as a delta
        // so the UI still gets the characters.
        events.push({ type: "text-delta", delta: payload });
      }
    }
  }
  return { events, rest: buffer };
}

/**
 * Pull the conversationId out of a Chatbase `finish` event. Returns `null` if the frame
 * doesn't carry one (e.g. Chatbase error frames). Handles three shapes:
 *   - v2 current: `{ messageMetadata: { conversationId } }`
 *   - v2 alias  : `{ metadata: { conversationId } }`
 *   - v2 flat   : `{ conversationId }`
 */
export function conversationIdFromFinishEvent(ev) {
  if (!ev || ev.type !== "finish") return null;
  const md = ev.messageMetadata || ev.metadata || {};
  return md.conversationId || ev.conversationId || null;
}
